/*
 * Conditional & Unconditional Generation: teaching simulator (Case Study, SL + HL)
 * ------------------------------------------------------------------------------
 * WHAT IT TEACHES
 *   Conditional generation is guided by an input, so the output matches an
 *   objective or style. Unconditional generation uses no input and relies on
 *   learned patterns, so results are more varied and unpredictable.
 *     • None (unconditional) ....... no input; each press gives a different,
 *       unpredictable output.
 *     • Class label (class-conditional / text-to-image) ... pick a class; the
 *       output is always an image of that class.
 *     • Sketch (image-to-image translation) ... draw an outline; the output
 *       follows that outline.
 *     • Segmentation map (image-to-image translation) ... label a few regions;
 *       the output places elements accordingly.
 *
 * This is an ILLUSTRATION, not real machine learning. Nothing is trained, no
 * dataset is loaded, no library is used. The mapping from input to output is a
 * simple stylised drawing rule.
 *
 * CONTROLS
 *   • Input type (buttons) .......... None / Class label / Sketch / Segmentation map.
 *   • Class (select) ................ shown for the class-label input.
 *   • Sketch canvas + presets + Clear  shown for the sketch input (draw with a
 *                                     pointer, or pick a preset shape so the
 *                                     control is keyboard operable).
 *   • Region pickers (buttons) ...... shown for the segmentation-map input; each
 *                                     cycles a region's label.
 *   • Generate (button) ............. produce a stylised output from the input.
 *   • Explainer accordion ........... conditional vs unconditional generation.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';

// ── brookebit palette (shared with the other simulators) ───────────────────────
const B = {
  bg:           '#faf7f2',
  surface:      '#ffffff',
  border:       'rgba(60,45,30,0.10)',
  borderStrong: 'rgba(60,45,30,0.16)',
  text:         '#2a241d',
  muted:        '#6b6055',
  faint:        '#9a8f82',
  green:        '#7BC19E',
  greenDark:    '#5fa882',
  greenSoft:    '#eef6f1',
  charcoal:     '#3A3D3C',
  blue:         '#5a7abf',
  blueDark:     '#3d5c9e',
  amber:        '#f0c74e',
  amberDark:    '#c9a12e',
  purple:       '#7a5bbf',
  purpleSoft:   '#efe7fb',
};

const font = "'Nunito', -apple-system, sans-serif";

const OW = 320, OH = 240;   // output canvas
const SW = 320, SH = 180;   // sketch input canvas

// Deterministic RNG so a generated output stays fixed on re-render (resize etc.)
function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ── Scene drawing (authored in a 240×180 space, scaled to fill the output) ─────
function drawFox0(ctx) {
  const IW = 240, IH = 180;
  const sky = ctx.createLinearGradient(0, 0, 0, IH);
  sky.addColorStop(0, '#eaf1f6'); sky.addColorStop(1, '#dfeae2');
  ctx.fillStyle = sky; ctx.fillRect(0, 0, IW, IH);
  ctx.fillStyle = '#cfe3d3'; ctx.fillRect(0, IH * 0.78, IW, IH * 0.22);
  const cx = IW / 2;
  ctx.fillStyle = '#e07a33';
  ctx.beginPath(); ctx.moveTo(cx - 58, 44); ctx.lineTo(cx - 30, 96); ctx.lineTo(cx - 78, 92); ctx.closePath(); ctx.fill();
  ctx.beginPath(); ctx.moveTo(cx + 58, 44); ctx.lineTo(cx + 30, 96); ctx.lineTo(cx + 78, 92); ctx.closePath(); ctx.fill();
  ctx.fillStyle = '#e8823c';
  ctx.beginPath();
  ctx.moveTo(cx - 62, 92);
  ctx.quadraticCurveTo(cx - 74, 118, cx - 40, 128);
  ctx.lineTo(cx, 168); ctx.lineTo(cx + 40, 128);
  ctx.quadraticCurveTo(cx + 74, 118, cx + 62, 92);
  ctx.quadraticCurveTo(cx, 66, cx - 62, 92);
  ctx.closePath(); ctx.fill();
  ctx.fillStyle = '#fbf6ef';
  ctx.beginPath(); ctx.moveTo(cx - 34, 116); ctx.lineTo(cx, 168); ctx.lineTo(cx + 34, 116);
  ctx.quadraticCurveTo(cx, 138, cx - 34, 116); ctx.closePath(); ctx.fill();
  ctx.fillStyle = '#2a241d';
  ctx.beginPath(); ctx.ellipse(cx - 26, 112, 7, 9, 0, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.ellipse(cx + 26, 112, 7, 9, 0, 0, Math.PI * 2); ctx.fill();
  ctx.beginPath(); ctx.moveTo(cx - 8, 146); ctx.lineTo(cx + 8, 146); ctx.lineTo(cx, 156); ctx.closePath(); ctx.fill();
}
function drawLandscape0(ctx) {
  const IW = 240, IH = 180;
  const sky = ctx.createLinearGradient(0, 0, 0, IH);
  sky.addColorStop(0, '#8fc4e8'); sky.addColorStop(1, '#d9eef7');
  ctx.fillStyle = sky; ctx.fillRect(0, 0, IW, IH);
  ctx.fillStyle = '#f6d268';
  ctx.beginPath(); ctx.arc(IW - 58, 46, 24, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = '#7fae7a';
  ctx.beginPath(); ctx.moveTo(0, 118);
  ctx.quadraticCurveTo(IW * 0.35, 74, IW * 0.7, 116);
  ctx.quadraticCurveTo(IW * 0.88, 138, IW, 110);
  ctx.lineTo(IW, IH); ctx.lineTo(0, IH); ctx.closePath(); ctx.fill();
  ctx.fillStyle = '#5d9457';
  ctx.beginPath(); ctx.moveTo(0, 140);
  ctx.quadraticCurveTo(IW * 0.4, 108, IW * 0.75, 142);
  ctx.quadraticCurveTo(IW * 0.9, 158, IW, 140);
  ctx.lineTo(IW, IH); ctx.lineTo(0, IH); ctx.closePath(); ctx.fill();
  ctx.fillStyle = '#6fa8cf';
  ctx.beginPath(); ctx.moveTo(0, 164);
  ctx.quadraticCurveTo(IW * 0.5, 156, IW, 166);
  ctx.lineTo(IW, IH); ctx.lineTo(0, IH); ctx.closePath(); ctx.fill();
}
function drawSkyline0(ctx) {
  const IW = 240, IH = 180;
  const sky = ctx.createLinearGradient(0, 0, 0, IH);
  sky.addColorStop(0, '#3b3b6b'); sky.addColorStop(0.55, '#c76a5a'); sky.addColorStop(1, '#f0b26b');
  ctx.fillStyle = sky; ctx.fillRect(0, 0, IW, IH);
  ctx.fillStyle = '#ffe0a3';
  ctx.beginPath(); ctx.arc(IW * 0.5, 128, 26, 0, Math.PI * 2); ctx.fill();
  const buildings = [[8, 96], [40, 70], [70, 116], [96, 58], [128, 88], [158, 44], [190, 100], [216, 74]];
  ctx.fillStyle = '#2c2b45';
  buildings.forEach(([x, top]) => ctx.fillRect(x, top, 26, IH - top));
  ctx.fillStyle = 'rgba(246,214,104,0.9)';
  buildings.forEach(([x, top]) => {
    for (let wy = top + 8; wy < IH - 8; wy += 12)
      for (let wx = x + 5; wx < x + 22; wx += 9)
        if ((wx + wy) % 3 !== 0) ctx.fillRect(wx, wy, 4, 5);
  });
}
const SCENES = { fox: drawFox0, landscape: drawLandscape0, skyline: drawSkyline0 };

function paintScene(ctx, scene, hueDeg) {
  ctx.save();
  ctx.filter = `hue-rotate(${hueDeg}deg)`;
  ctx.scale(OW / 240, OH / 180);
  (SCENES[scene] || drawFox0)(ctx);
  ctx.restore();
}

// Abstract fallback for the unconditional mode: random gradient + shapes.
function drawAbstract(ctx, rng) {
  const h1 = rng() * 360, h2 = (h1 + 60 + rng() * 120) % 360;
  const g = ctx.createLinearGradient(0, 0, OW, OH);
  g.addColorStop(0, `hsl(${h1}, 55%, 78%)`);
  g.addColorStop(1, `hsl(${h2}, 55%, 60%)`);
  ctx.fillStyle = g; ctx.fillRect(0, 0, OW, OH);
  const n = 4 + Math.floor(rng() * 4);
  for (let i = 0; i < n; i++) {
    const hue = rng() * 360;
    ctx.fillStyle = `hsla(${hue}, 65%, 55%, 0.82)`;
    ctx.strokeStyle = `hsl(${hue}, 60%, 34%)`; ctx.lineWidth = 2;
    const x = rng() * OW, y = rng() * OH, s = 24 + rng() * 70;
    const kind = Math.floor(rng() * 3);
    ctx.beginPath();
    if (kind === 0) ctx.arc(x, y, s / 2, 0, Math.PI * 2);
    else if (kind === 1) ctx.rect(x - s / 2, y - s / 2, s, s);
    else { ctx.moveTo(x, y - s / 2); ctx.lineTo(x + s / 2, y + s / 2); ctx.lineTo(x - s / 2, y + s / 2); ctx.closePath(); }
    ctx.fill(); ctx.stroke();
  }
}

// ── Segmentation-map bands ─────────────────────────────────────────────────────
const SEG_LABELS = ['sky', 'hills', 'water', 'grass', 'forest'];
const SEG_META = {
  sky:    { label: 'Sky',    swatch: '#8fc4e8' },
  hills:  { label: 'Hills',  swatch: '#5d9457' },
  water:  { label: 'Water',  swatch: '#6fa8cf' },
  grass:  { label: 'Grass',  swatch: '#a9d18e' },
  forest: { label: 'Forest', swatch: '#2f6b3f' },
};
function drawBand(ctx, y, w, h, label) {
  switch (label) {
    case 'sky': {
      const g = ctx.createLinearGradient(0, y, 0, y + h);
      g.addColorStop(0, '#8fc4e8'); g.addColorStop(1, '#d9eef7');
      ctx.fillStyle = g; ctx.fillRect(0, y, w, h);
      ctx.fillStyle = '#f6d268';
      ctx.beginPath(); ctx.arc(w - 52, y + Math.min(34, h * 0.4), 18, 0, Math.PI * 2); ctx.fill();
      break;
    }
    case 'water': {
      ctx.fillStyle = '#6fa8cf'; ctx.fillRect(0, y, w, h);
      ctx.strokeStyle = 'rgba(255,255,255,0.5)'; ctx.lineWidth = 2;
      for (let wy = y + 12; wy < y + h; wy += 16) {
        ctx.beginPath();
        for (let wx = 0; wx <= w; wx += 20) ctx.lineTo(wx, wy + Math.sin(wx / 14) * 3);
        ctx.stroke();
      }
      break;
    }
    case 'grass': {
      ctx.fillStyle = '#a9d18e'; ctx.fillRect(0, y, w, h);
      ctx.strokeStyle = '#7fae5f'; ctx.lineWidth = 1.5;
      for (let wx = 8; wx < w; wx += 14) {
        ctx.beginPath(); ctx.moveTo(wx, y + h); ctx.lineTo(wx + 2, y + h - 12); ctx.stroke();
      }
      break;
    }
    case 'forest': {
      ctx.fillStyle = '#2f6b3f'; ctx.fillRect(0, y, w, h);
      ctx.fillStyle = '#3f8a52';
      for (let wx = 16; wx < w; wx += 30) {
        ctx.beginPath(); ctx.moveTo(wx, y + h - 6); ctx.lineTo(wx + 12, y + h - 34); ctx.lineTo(wx + 24, y + h - 6); ctx.closePath(); ctx.fill();
      }
      break;
    }
    case 'hills':
    default: {
      ctx.fillStyle = '#5d9457'; ctx.fillRect(0, y, w, h);
      ctx.fillStyle = '#6fa869';
      ctx.beginPath(); ctx.moveTo(0, y + h);
      ctx.quadraticCurveTo(w * 0.3, y + h * 0.2, w * 0.55, y + h * 0.55);
      ctx.quadraticCurveTo(w * 0.8, y + h * 0.85, w, y + h * 0.4);
      ctx.lineTo(w, y + h); ctx.closePath(); ctx.fill();
      break;
    }
  }
}
function drawSeg(ctx, regions) {
  const h = OH / regions.length;
  regions.forEach((label, i) => drawBand(ctx, i * h, OW, h, label));
}

// ── Sketch output: follow the drawn outline ────────────────────────────────────
function drawSketchOut(ctx, strokes, rng) {
  const g = ctx.createLinearGradient(0, 0, 0, OH);
  g.addColorStop(0, '#eef2f7'); g.addColorStop(1, '#e7eee9');
  ctx.fillStyle = g; ctx.fillRect(0, 0, OW, OH);
  const sx = OW / SW, sy = OH / SH;
  strokes.forEach((pts) => {
    if (!pts || pts.length < 2) return;
    const hue = rng() * 360;
    // Fill the traced region
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(pts[0].x * sx, pts[0].y * sy);
    for (let k = 1; k < pts.length; k++) ctx.lineTo(pts[k].x * sx, pts[k].y * sy);
    ctx.closePath();
    const fg = ctx.createLinearGradient(0, 0, OW, OH);
    fg.addColorStop(0, `hsl(${hue}, 70%, 62%)`);
    fg.addColorStop(1, `hsl(${(hue + 50) % 360}, 70%, 46%)`);
    ctx.fillStyle = fg; ctx.globalAlpha = 0.85; ctx.fill(); ctx.globalAlpha = 1;
    ctx.restore();
    // Trace the outline thickly
    ctx.lineJoin = 'round'; ctx.lineCap = 'round';
    ctx.lineWidth = 6; ctx.strokeStyle = `hsl(${(hue + 20) % 360}, 60%, 30%)`;
    ctx.beginPath();
    ctx.moveTo(pts[0].x * sx, pts[0].y * sy);
    for (let k = 1; k < pts.length; k++) ctx.lineTo(pts[k].x * sx, pts[k].y * sy);
    ctx.stroke();
  });
}

// ── Preset sketch outlines (also make the sketch mode keyboard operable) ───────
const PRESETS = {
  circle: (() => { const p = []; for (let a = 0; a <= 360; a += 12) { const r = a * Math.PI / 180; p.push({ x: 160 + Math.cos(r) * 62, y: 90 + Math.sin(r) * 62 }); } return p; })(),
  mountain: [{ x: 24, y: 150 }, { x: 84, y: 58 }, { x: 132, y: 108 }, { x: 196, y: 44 }, { x: 296, y: 150 }, { x: 24, y: 150 }],
  house: [{ x: 96, y: 150 }, { x: 96, y: 92 }, { x: 160, y: 50 }, { x: 224, y: 92 }, { x: 224, y: 150 }, { x: 96, y: 150 }],
};

// ── Mode metadata ──────────────────────────────────────────────────────────────
function modeMeta(type) {
  switch (type) {
    case 'class':  return { kind: 'Conditional', term: 'class-conditional (text-to-image)', note: 'constrained to your chosen class' };
    case 'sketch': return { kind: 'Conditional', term: 'image-to-image translation', note: 'follows your drawn outline' };
    case 'seg':    return { kind: 'Conditional', term: 'image-to-image translation (segmentation map)', note: 'follows your segmentation map' };
    case 'none':
    default:       return { kind: 'Unconditional', term: 'no input', note: 'varied and unpredictable' };
  }
}

const INPUT_TYPES = [
  { id: 'none',   label: 'None' },
  { id: 'class',  label: 'Class label' },
  { id: 'sketch', label: 'Sketch' },
  { id: 'seg',    label: 'Segmentation map' },
];
const CLASSES = [
  { id: 'fox',       label: 'Animal (fox)' },
  { id: 'landscape', label: 'Landscape' },
  { id: 'skyline',   label: 'City skyline' },
];

// ── Main component ─────────────────────────────────────────────────────────────
export default function ConditionalGenerationSimulator({ onBack }) {
  const outRef = useRef(null);
  const sketchRef = useRef(null);
  const drawingRef = useRef(false);

  const [inputType, setInputType] = useState('none');
  const [classId,   setClassId]   = useState('fox');
  const [regions,   setRegions]   = useState(['sky', 'hills', 'water']);
  const [strokes,   setStrokes]   = useState([]);      // array of strokes; each = [{x,y}] in SW×SH
  const [gen,       setGen]       = useState(null);    // last generation descriptor
  const [count,     setCount]     = useState(0);
  const [hintOpen,  setHintOpen]  = useState(false);

  // ── Render the output whenever a new generation is produced ──────────────────
  useEffect(() => {
    const c = outRef.current;
    if (!c) return;
    c.width = OW; c.height = OH;
    const ctx = c.getContext('2d');
    ctx.clearRect(0, 0, OW, OH);
    if (!gen) {
      ctx.fillStyle = B.bg; ctx.fillRect(0, 0, OW, OH);
      ctx.fillStyle = B.faint; ctx.font = `600 15px ${font}`;
      ctx.textAlign = 'center';
      ctx.fillText('Press Generate to produce an output', OW / 2, OH / 2);
      return;
    }
    const rng = mulberry32(gen.seed);
    if (gen.type === 'none') {
      const pick = Math.floor(rng() * 5);
      if (pick < 3) paintScene(ctx, ['fox', 'landscape', 'skyline'][pick], rng() * 360);
      else drawAbstract(ctx, rng);
    } else if (gen.type === 'class') {
      paintScene(ctx, gen.classId, rng() * 40 - 20);
    } else if (gen.type === 'sketch') {
      drawSketchOut(ctx, gen.strokes, rng);
    } else if (gen.type === 'seg') {
      drawSeg(ctx, gen.regions);
    }
  }, [gen]);

  // ── Redraw the sketch input canvas as the user draws ─────────────────────────
  useEffect(() => {
    if (inputType !== 'sketch') return;
    const c = sketchRef.current;
    if (!c) return;
    c.width = SW; c.height = SH;
    const ctx = c.getContext('2d');
    ctx.fillStyle = B.surface; ctx.fillRect(0, 0, SW, SH);
    // dotted guide border
    ctx.strokeStyle = B.border; ctx.setLineDash([4, 4]); ctx.lineWidth = 1;
    ctx.strokeRect(4, 4, SW - 8, SH - 8); ctx.setLineDash([]);
    if (!strokes.length) {
      ctx.fillStyle = B.faint; ctx.font = `600 13px ${font}`; ctx.textAlign = 'center';
      ctx.fillText('Draw an outline here (or pick a preset shape)', SW / 2, SH / 2);
    }
    ctx.strokeStyle = B.charcoal; ctx.lineJoin = 'round'; ctx.lineCap = 'round'; ctx.lineWidth = 3;
    strokes.forEach((pts) => {
      if (!pts.length) return;
      ctx.beginPath(); ctx.moveTo(pts[0].x, pts[0].y);
      for (let k = 1; k < pts.length; k++) ctx.lineTo(pts[k].x, pts[k].y);
      ctx.stroke();
    });
  }, [strokes, inputType]);

  // ── Sketch pointer handlers ──────────────────────────────────────────────────
  const pointFromEvent = (e) => {
    const c = sketchRef.current;
    const rect = c.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (c.width / rect.width),
      y: (e.clientY - rect.top) * (c.height / rect.height),
    };
  };
  const onPointerDown = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    sketchRef.current.setPointerCapture?.(e.pointerId);
    const p = pointFromEvent(e);
    setStrokes((prev) => [...prev, [p]]);
  };
  const onPointerMove = (e) => {
    if (!drawingRef.current) return;
    const p = pointFromEvent(e);
    setStrokes((prev) => {
      if (!prev.length) return prev;
      const copy = prev.slice();
      copy[copy.length - 1] = [...copy[copy.length - 1], p];
      return copy;
    });
  };
  const onPointerUp = () => { drawingRef.current = false; };

  const loadPreset = useCallback((name) => { setStrokes([PRESETS[name].map((p) => ({ ...p }))]); }, []);
  const clearSketch = useCallback(() => setStrokes([]), []);

  const cycleRegion = useCallback((idx) => {
    setRegions((prev) => {
      const copy = prev.slice();
      const cur = SEG_LABELS.indexOf(copy[idx]);
      copy[idx] = SEG_LABELS[(cur + 1) % SEG_LABELS.length];
      return copy;
    });
  }, []);

  const sketchEmpty = inputType === 'sketch' && !strokes.some((s) => s.length > 1);

  const handleGenerate = useCallback(() => {
    if (sketchEmpty) return;
    const seed = (Math.random() * 1e9) | 0;
    const g = { type: inputType, seed };
    if (inputType === 'class') g.classId = classId;
    if (inputType === 'seg') g.regions = [...regions];
    if (inputType === 'sketch') g.strokes = strokes.map((s) => s.map((p) => ({ ...p })));
    setGen(g);
    setCount((c) => c + 1);
  }, [inputType, classId, regions, strokes, sketchEmpty]);

  const genMeta = gen ? modeMeta(gen.type) : null;
  const outLabel = !gen ? 'No output yet.'
    : gen.type === 'none'   ? 'An unconditional output: a random image the model invented from learned patterns.'
    : gen.type === 'class'  ? `A class-conditional output constrained to the ${CLASSES.find((c) => c.id === gen.classId)?.label} class.`
    : gen.type === 'sketch' ? 'An image-to-image output that follows your drawn outline.'
    : 'An image-to-image output that follows your segmentation map.';

  // ── Styles (shared vocabulary with the other simulators) ─────────────────────
  const s = {
    wrap: { fontFamily: font, background: B.bg, minHeight: '100vh', WebkitFontSmoothing: 'antialiased' },
    topbar: {
      position: 'sticky', top: 0, zIndex: 100, background: B.surface,
      borderBottom: `1px solid ${B.border}`, display: 'flex', alignItems: 'center',
      padding: '8px 1.5rem', minHeight: 60, gap: '0.75rem', flexWrap: 'wrap',
    },
    backBtn: {
      background: 'none', border: 'none', cursor: 'pointer', color: B.muted,
      fontFamily: font, fontSize: '0.85rem', fontWeight: 600, display: 'flex',
      alignItems: 'center', gap: 5, padding: '4px 8px', borderRadius: 6,
    },
    title: { fontSize: '1rem', fontWeight: 700, color: B.text, margin: 0 },
    badge: {
      fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.04em',
      background: B.greenSoft, color: B.greenDark, padding: '3px 8px', borderRadius: 6,
    },
    lvlBadge: {
      fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.06em',
      background: B.purpleSoft, color: B.purple, padding: '3px 7px', borderRadius: 6,
    },
    body: { maxWidth: 820, margin: '0 auto', padding: '2rem 1.5rem 3rem' },
    card: {
      background: B.surface, border: `1px solid ${B.border}`, borderRadius: 14,
      padding: '1.25rem', marginBottom: '1rem',
    },
    sectionLabel: {
      fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em',
      color: B.faint, textTransform: 'uppercase', marginBottom: '0.6rem',
    },
    segRow: { display: 'flex', gap: '0.5rem', flexWrap: 'wrap' },
    typeBtn: (active) => ({
      fontFamily: font, fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer',
      border: `1.5px solid ${active ? B.greenDark : B.borderStrong}`,
      background: active ? B.greenSoft : B.bg, color: active ? B.greenDark : B.text,
      borderRadius: 9, padding: '8px 13px',
    }),
    select: {
      fontFamily: font, fontSize: '0.88rem', border: `1.5px solid ${B.borderStrong}`,
      borderRadius: 8, padding: '6px 10px', background: B.bg, color: B.text, cursor: 'pointer',
    },
    btnPrimary: {
      fontFamily: font, fontSize: '0.9rem', fontWeight: 700, cursor: 'pointer',
      background: B.green, color: '#fff', border: 'none', borderRadius: 9,
      padding: '10px 20px', display: 'inline-flex', alignItems: 'center', gap: 7,
    },
    btnGhost: {
      fontFamily: font, fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer',
      background: B.bg, color: B.text, border: `1.5px solid ${B.borderStrong}`,
      borderRadius: 9, padding: '7px 13px', display: 'inline-flex', alignItems: 'center', gap: 6,
    },
    canvasOut: {
      display: 'block', width: '100%', maxWidth: OW, height: 'auto', borderRadius: 10,
      border: `1px solid ${B.border}`, background: B.bg, margin: '0 auto', aspectRatio: `${OW} / ${OH}`,
    },
    sketchCanvas: {
      display: 'block', width: '100%', height: 'auto', borderRadius: 10,
      border: `1px solid ${B.border}`, background: B.surface, touchAction: 'none',
      cursor: 'crosshair', aspectRatio: `${SW} / ${SH}`,
    },
    expBtn: {
      width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      background: 'none', border: 'none', cursor: 'pointer', fontFamily: font,
      fontSize: '0.9rem', fontWeight: 600, color: B.text, padding: '0.9rem 1.25rem', borderRadius: 14,
    },
    expBody: { padding: '0 1.25rem 1.25rem', borderTop: `1px solid ${B.border}` },
    expGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '0.9rem' },
    expCard: { padding: '0.9rem', borderRadius: 8, border: `1px solid ${B.border}` },
    expCardTitle: { fontSize: '0.85rem', fontWeight: 700, color: B.text, marginBottom: '0.5rem' },
    expList: { fontSize: '0.8rem', color: B.muted, lineHeight: 1.65, paddingLeft: '1rem', margin: 0 },
    expNote: { fontSize: '0.78rem', color: B.faint, lineHeight: 1.6, marginTop: '0.75rem' },
  };

  return (
    <div style={s.wrap}>
      {/* ── Topbar ─────────────────────────────────────────────────────── */}
      <header style={s.topbar}>
        {onBack && (
          <button style={s.backBtn} onClick={onBack} aria-label="Back">
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="10 3 5 8 10 13" />
            </svg>
            Back
          </button>
        )}
        <a href="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none', flexShrink: 0 }}>
          <img src="/logo.svg" alt="brookebit" height={40} style={{ display: 'block' }} />
        </a>
        <h1 style={s.title}>Conditional Generation</h1>
        <span style={s.badge}>Case Study</span>
        <span style={s.lvlBadge}>SL · HL</span>
      </header>

      {/* ── Body ───────────────────────────────────────────────────────── */}
      <main style={s.body}>

        {/* ── Input selector + editor ────────────────────────────────── */}
        <div style={s.card}>
          <div style={s.sectionLabel}>Input type</div>
          <div style={s.segRow} role="group" aria-label="Input type">
            {INPUT_TYPES.map((t) => (
              <button key={t.id} style={s.typeBtn(inputType === t.id)}
                aria-pressed={inputType === t.id} onClick={() => setInputType(t.id)}>
                {t.label}
              </button>
            ))}
          </div>

          {/* Per-type editor */}
          <div style={{ marginTop: '1rem' }}>
            {inputType === 'none' && (
              <p style={{ fontSize: '0.82rem', color: B.muted, lineHeight: 1.55, margin: 0 }}>
                <strong>Unconditional:</strong> no input is given. Each press of Generate produces a
                different image the model invents from its learned patterns, so you cannot control what appears.
              </p>
            )}

            {inputType === 'class' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <label style={{ fontSize: '0.72rem', fontWeight: 700, color: B.muted, textTransform: 'uppercase', letterSpacing: '0.04em' }} htmlFor="class-select">
                  Class (a tiny text-to-image prompt)
                </label>
                <select id="class-select" style={s.select} value={classId} onChange={(e) => setClassId(e.target.value)}>
                  {CLASSES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
                <span style={{ fontSize: '0.76rem', color: B.faint }}>The output will always be an image of this class.</span>
              </div>
            )}

            {inputType === 'sketch' && (
              <div>
                <canvas
                  ref={sketchRef}
                  style={s.sketchCanvas}
                  onPointerDown={onPointerDown}
                  onPointerMove={onPointerMove}
                  onPointerUp={onPointerUp}
                  onPointerLeave={onPointerUp}
                  aria-label="Sketch input canvas. Draw an outline with a pointer, or use the preset-shape buttons below."
                />
                <div style={{ ...s.segRow, marginTop: '0.6rem', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.74rem', color: B.faint }}>Preset shape:</span>
                  <button style={s.btnGhost} onClick={() => loadPreset('circle')}>Circle</button>
                  <button style={s.btnGhost} onClick={() => loadPreset('mountain')}>Mountain</button>
                  <button style={s.btnGhost} onClick={() => loadPreset('house')}>House</button>
                  <button style={{ ...s.btnGhost, marginLeft: 'auto' }} onClick={clearSketch}>Clear</button>
                </div>
              </div>
            )}

            {inputType === 'seg' && (
              <div>
                <span style={{ fontSize: '0.76rem', color: B.faint }}>
                  Label each region (top to bottom). Activate a region to cycle its label.
                </span>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 8, maxWidth: 360 }}>
                  {regions.map((label, i) => (
                    <button key={i} onClick={() => cycleRegion(i)}
                      aria-label={`Region ${i + 1} of ${regions.length}, currently ${SEG_META[label].label}. Activate to change.`}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
                        border: `1.5px solid ${B.borderStrong}`, borderRadius: 9,
                        background: B.bg, padding: '9px 12px', fontFamily: font,
                        fontSize: '0.85rem', fontWeight: 700, color: B.text, textAlign: 'left',
                      }}>
                      <span style={{ width: 22, height: 22, borderRadius: 5, background: SEG_META[label].swatch, border: `1px solid ${B.border}`, flexShrink: 0 }} />
                      <span style={{ color: B.faint, fontSize: '0.72rem', width: 74 }}>
                        {i === 0 ? 'Top region' : i === regions.length - 1 ? 'Bottom region' : 'Middle region'}
                      </span>
                      {SEG_META[label].label}
                      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke={B.faint}
                        strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                        aria-hidden="true" style={{ marginLeft: 'auto' }}>
                        <polyline points="5 3 11 8 5 13" />
                      </svg>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginTop: '1.1rem', flexWrap: 'wrap' }}>
            <button
              style={{ ...s.btnPrimary, ...(sketchEmpty ? { opacity: 0.45, cursor: 'not-allowed' } : {}) }}
              onClick={handleGenerate} disabled={sketchEmpty}
              title={sketchEmpty ? 'Draw an outline or pick a preset shape first.' : undefined}
              aria-label="Generate an output from the current input">
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor"
                strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M8 1.5l1.6 3.9 3.9 1.6-3.9 1.6L8 12.5 6.4 8.6 2.5 7l3.9-1.6z" />
              </svg>
              Generate
            </button>
            <span style={{ fontSize: '0.76rem', color: B.faint }}>
              {count > 0 ? `${count} generated · ` : ''}Mode:{' '}
              <strong style={{ color: inputType === 'none' ? B.amberDark : B.greenDark }}>{modeMeta(inputType).kind}</strong>
              {' '}({modeMeta(inputType).term})
            </span>
          </div>
        </div>

        {/* ── Output ─────────────────────────────────────────────────── */}
        <div style={s.card}>
          <div style={s.sectionLabel}>
            Output{genMeta ? ` · ${genMeta.kind} · ${genMeta.note}` : ''}
          </div>
          <canvas ref={outRef} style={s.canvasOut} role="img" aria-label={outLabel} />
        </div>

        {/* ── Contrast line ──────────────────────────────────────────── */}
        <div style={{ ...s.card, background: B.greenSoft, borderColor: 'rgba(95,168,130,0.35)' }}>
          <p style={{ fontSize: '0.85rem', color: B.text, lineHeight: 1.6, margin: 0 }}>
            <strong>Conditional</strong> generation is guided by your input, so the output matches an
            objective or style. <strong>Unconditional</strong> generation uses no input and relies on
            learned patterns, so results are more <strong>varied and unpredictable</strong>.
          </p>
        </div>

        {/* ── Explainer accordion ────────────────────────────────────── */}
        <div style={{ ...s.card, padding: 0, overflow: 'hidden' }}>
          <button style={s.expBtn} onClick={() => setHintOpen((o) => !o)} aria-expanded={hintOpen}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke={B.green}
                strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M6 2h4M8 2v3M4.5 5.5A4 4 0 1 0 11.5 5.5" />
                <line x1="8" y1="9" x2="8" y2="12" />
                <circle cx="8" cy="13.5" r="0.5" fill={B.green} stroke="none" />
              </svg>
              Conditional vs unconditional generation
            </span>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke={B.green}
              strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
              style={{ transform: hintOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
              <polyline points="3 6 8 11 13 6" />
            </svg>
          </button>

          {hintOpen && (
            <div style={s.expBody}>
              <p style={{ fontSize: '0.85rem', color: B.text, lineHeight: 1.6, marginTop: '0.9rem' }}>
                A generative model can run <strong>with</strong> or <strong>without</strong> a guiding input.
                With an input it does <strong>conditional</strong> generation; without one it does{' '}
                <strong>unconditional</strong> generation, sampling freely from what it has learned.
              </p>

              <div style={s.expGrid}>
                <div style={s.expCard}>
                  <div style={s.expCardTitle}>Conditional</div>
                  <ul style={s.expList}>
                    <li>A <strong>class label</strong> or text prompt gives <strong>class-conditional</strong> or <strong>text-to-image generation</strong>.</li>
                    <li>A <strong>sketch</strong> or <strong>segmentation map</strong> gives <strong>image-to-image translation</strong>.</li>
                    <li>The output is <strong>constrained</strong> to match the input, an objective or a style.</li>
                  </ul>
                </div>
                <div style={s.expCard}>
                  <div style={s.expCardTitle}>Unconditional</div>
                  <ul style={s.expList}>
                    <li>There is <strong>no input</strong> to steer the result.</li>
                    <li>The model relies only on its <strong>learned patterns</strong>.</li>
                    <li>Outputs are more <strong>varied and unpredictable</strong>, and you cannot control the content.</li>
                  </ul>
                </div>
              </div>

              <p style={s.expNote}>
                Try it: with <strong>None</strong>, press Generate several times and notice each output is
                different. Then pick a <strong>class label</strong>, draw a <strong>sketch</strong>, or set a{' '}
                <strong>segmentation map</strong> and see the output become constrained to match the input.
              </p>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
